import {
  createSlice,
  createAsyncThunk,
  createSelector,
} from '@reduxjs/toolkit';
import { objKeyAsString, Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import FiltersSettingsApi from './FiltersSettingsApi';
import { RootState } from 'state/store';
import { PersistentFilterTypeKeys } from 'utils/types/filters';

interface FiltersSlice {
  filters: {
    [key: string]: {
      id: string;
      type: string;
      settings: objKeyAsString;
    };
  };
  state: Status;
}

export interface NewFilterSettings {
  filter_type: PersistentFilterTypeKeys;
  filter_settings: objKeyAsString;
}

const filtersSettingsApi = FiltersSettingsApi;

/* ============================= INITIAL STATE ============================== */
const initialState: FiltersSlice = {
  filters: {},
  state: SLICE_STATUS.IDLE,
};

/* ============================== REDUX THUNK =============================== */
export const fetchFilterSettingByType = createAsyncThunk(
  'filtersSettings/FETCH_FILTER_SETTING_BY_ID',
  async (filterType: PersistentFilterTypeKeys, { rejectWithValue }) => {
    try {
      const response = await filtersSettingsApi.retrieveFilterSettingByType(
        filterType
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createFilterSetting = createAsyncThunk(
  'filtersSettings/CREATE_FILTER_SETTING',
  async (newFilterSettings: NewFilterSettings, { rejectWithValue }) => {
    try {
      const response = await filtersSettingsApi.createFilterSetting(
        newFilterSettings
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateFilterSetting = createAsyncThunk(
  'filtersSettings/UPDATE_FILTER_SETTIN',
  async (
    params: { id: string; updateFields: { filter_settings: objKeyAsString } },
    { rejectWithValue }
  ) => {
    try {
      const response = await filtersSettingsApi.updateFilterSetting(
        params.id,
        params.updateFields
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
/* ================================= REDUCER ================================ */
const filtersSlice = createSlice({
  name: 'filtersSettings',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFilterSettingByType.pending, (state) => {
        state.state = SLICE_STATUS.LOADING;
      })
      .addCase(fetchFilterSettingByType.fulfilled, (state, action) => {
        state.state = SLICE_STATUS.IDLE;
        if (action.payload) {
          state.filters = {
            ...state.filters,
            [action.payload.filter_type]: {
              id: action.payload.id,
              type: action.payload.filter_type,
              settings: action.payload.filter_settings,
            },
          };
        }
      })
      .addCase(fetchFilterSettingByType.rejected, (state) => {
        state.state = SLICE_STATUS.FAILED;
      })
      .addCase(createFilterSetting.pending, (state) => {
        state.state = SLICE_STATUS.LOADING;
      })
      .addCase(createFilterSetting.fulfilled, (state, action) => {
        state.state = SLICE_STATUS.IDLE;
        if (!action.payload) {
          return;
        }
        state.filters = {
          ...state.filters,
          [action.payload.filter_type]: {
            id: action.payload.id,
            type: action.payload.filter_type,
            settings: action.payload.filter_settings,
          },
        };
      })
      .addCase(createFilterSetting.rejected, (state) => {
        state.state = SLICE_STATUS.FAILED;
      })
      .addCase(updateFilterSetting.pending, (state) => {
        state.state = SLICE_STATUS.LOADING;
      })
      .addCase(updateFilterSetting.fulfilled, (state, action) => {
        state.state = SLICE_STATUS.IDLE;
        if (!action.payload) {
          return;
        }
        state.filters = {
          ...state.filters,
          [action.payload.filter_type]: {
            id: action.payload.id,
            type: action.payload.filter_type,
            settings: action.payload.filter_settings,
          },
        };
      })
      .addCase(updateFilterSetting.rejected, (state) => {
        state.state = SLICE_STATUS.FAILED;
      });
  },
});

/* =============================== SELECTORS ================================ */
/**
 * INVARIANT: the selector returned here must hand back a referentially stable
 * value while the stored filter is unchanged, so one instance is memoized per
 * filterType. Building the projection inline returned a fresh object literal on
 * every call, and `useSelector`'s reference comparison then re-rendered every
 * consumer on every action dispatched anywhere in the app — which on list pages
 * (requests, tasks, projects, budgets...) means re-reconciling every loaded row.
 *
 * The Map is keyed by the fixed SETTINGS_FILTERS constants, so it stays bounded.
 */
const filtersSettingsByTypeSelectors = new Map<
  string,
  (store: RootState) => { settings: objKeyAsString; id: string } | null
>();

export const selectFiltersSettingsByType = (filterType: string) => {
  const cached = filtersSettingsByTypeSelectors.get(filterType);
  if (cached) return cached;

  const selector = createSelector(
    (store: RootState) => store.filtersSettings.filters[filterType],
    (filter) => (filter ? { settings: filter.settings, id: filter.id } : null)
  );

  filtersSettingsByTypeSelectors.set(filterType, selector);
  return selector;
};

export const selectFiltersSliceStatus = (store: RootState) =>
  store.filtersSettings.state;

export const selectFiltersSettingsTypeId = createSelector(
  [
    (state: RootState, filterType: string) =>
      selectFiltersSettingsByType(filterType)(state),
  ],
  (filterSettings) => {
    if (filterSettings) {
      return filterSettings?.id;
    }
    return null;
  }
);

export default filtersSlice.reducer;
